import { useCallback, useMemo, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { alpha, Box, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import { useCanEdit } from '~/hooks'

const Dropzone = (props) => {
  const {
    accept,
    children,
    disabled: disabledProp,
    maxFileSize = 2 * 1024 * 1024,
    multiple = true,
    sx = {},
    onDrop: onDropProp,
    ...rest
  } = props
  const { t } = useTranslation()
  const canEdit = useCanEdit()
  const disabled = disabledProp || !canEdit

  const [errors, setErrors] = useState()

  const onDrop = useCallback(
    (files) => {
      if (!files?.length) return
      if (files.length > 1 && !multiple) {
        setErrors([t('upload.only-one-file')])
        return
      }
      const errors = files.reduce((acc, file) => {
        if (!accept[file.type]) {
          acc.push(t('upload.bad-file-format', { file: file.name }))
        } else if (file.size > maxFileSize) {
          acc.push(
            t('upload.too-big', {
              file: file.name,
              size: (maxFileSize / 1024).toFixed(1) + 'KB',
            })
          )
        }
        return acc
      }, [])
      if (!errors.length) {
        onDropProp?.(files)
        setErrors()
      } else {
        setErrors(errors)
      }
    },
    [accept, multiple, onDropProp, t, maxFileSize]
  )

  const info = useMemo(() => {
    return (
      children ?? (
        <>
          {t(multiple ? 'dropzone.cta.multiple' : 'dropzone.cta.single')}
          <br />
          <Typography variant="annotation">
            {t('dropzone.accept-types', {
              types: Object.values(accept)
                .map((types) =>
                  types.map((type) => type.replace(/^\./, '')).join(', ')
                )
                .join(', '),
            })}
          </Typography>
        </>
      )
    )
  }, [accept, children, multiple, t])

  const [focused, setFocused] = useState(false)

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept,
    disabled,
    multiple,
    onDrop,
  })

  const rootProps = getRootProps()

  return (
    <Box
      sx={{
        position: 'relative',
        backgroundColor: (theme) =>
          isDragActive
            ? alpha(theme.palette.text.primary, 0.1)
            : alpha(theme.palette.text.primary, 0.05),
        borderRadius: '4px',
        overflow: 'clip',
        minHeight: '200px',
        cursor: disabled ? 'default' : undefined,
        p: 2,
        ...(focused
          ? { border: 'none', outline: 'none' }
          : {
              '&:hover .notchedOutline': {
                border: (theme) =>
                  `1px solid ${theme.palette.input.border.hover}`,
              },
            }),
        ...sx,
      }}
      {...rest}
      {...rootProps}
      onBlur={(e) => {
        rootProps.onBlur?.(e)
        setFocused(false)
      }}
      onFocus={(e) => {
        rootProps.onFocus?.(e)
        setFocused(true)
      }}
    >
      <input {...getInputProps()} />
      {errors?.length ? (
        <Typography
          dangerouslySetInnerHTML={{ __html: errors?.join('<br />') }}
          sx={{ color: 'danger' }}
          variant="body2"
        />
      ) : (
        (children ?? info)
      )}
      <Box
        aria-hidden
        className="notchedOutline"
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          borderRadius: '4px',
          ...(focused
            ? {
                border: (theme) =>
                  `2px solid ${theme.palette.input.border.focused}`,
              }
            : {
                border: (theme) =>
                  `1px solid ${theme.palette.input.border.default}`,
              }),
        }}
      />
    </Box>
  )
}

Dropzone.propTypes = {
  accept: PropTypes.object,
  disabled: PropTypes.bool,
  maxFileSize: PropTypes.number,
  multiple: PropTypes.bool,
  sx: PropTypes.object,
  onDrop: PropTypes.func,
}

export default Dropzone
